import { useState, useCallback, useMemo, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { useOrders } from '../hooks/useOrders'
import { KANBAN_COLUNAS, CIDADES_ABAS, detectarCidadeAba } from '../data/kanban'
import { getTags, saveTag, deleteTag, toggleTagPedido, savePedido } from '../utils/storage'
import { v4 as uuid } from 'uuid'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar, Package, AlertCircle, Tag, Plus, X, Check, Filter, Trash2, Eye, EyeOff } from 'lucide-react'
import { usePedidosFiltros } from '../hooks/usePedidosFiltros'
import { FiltroDrawer } from '../components/FiltroDrawer'
import { formatCurrency } from '../utils/fmt'

const STATUS_STYLE = {
  CADASTRADO: '#94A3B8', FABRICANDO: '#FBBF24', EXPEDICAO: '#4ADE80',
  ROTA: '#FB923C', ENTREGUE: '#F9A8D4', AJUSTE: '#93C5FD',
  A_FABRICAR: '#94A3B8', FABRICADO: '#93C5FD', POS_VENDA: '#F9A8D4',
}

const TAG_CORES = [
  '#EF4444','#F97316','#EAB308','#22C55E',
  '#06B6D4','#3B82F6','#8B5CF6','#EC4899',
  '#111111','#64748B',
]

function formatDate(d) {
  if (!d) return null
  try { return format(new Date(d + 'T12:00:00'), "dd/MM/yy", { locale: ptBR }) } catch { return null }
}

function isPrazoVencido(prazo) {
  if (!prazo) return false
  return new Date(prazo + 'T12:00:00') < new Date()
}


// ── Tag Chip ─────────────────────────────────────────────────────────────────
function TagChip({ tag, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
      style={{ backgroundColor: tag.cor }}
    >
      {tag.nome}
      {onRemove && (
        <button type="button" onClick={e => { e.stopPropagation(); onRemove(tag.id) }}
          className="hover:opacity-70 transition-opacity">
          <X size={9} />
        </button>
      )}
    </span>
  )
}

// ── Tag Picker (dropdown no card) ─────────────────────────────────────────────
function TagPicker({ pedidoId, pedidoTags = [], allTags, onToggle, onClose }) {
  return (
    <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-52"
      onClick={e => e.stopPropagation()}
    >
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Etiquetas</p>
      {allTags.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nenhuma etiqueta criada</p>
      )}
      <div className="space-y-1">
        {allTags.map(tag => {
          const ativa = pedidoTags.includes(tag.id)
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onToggle(pedidoId, tag.id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center"
                style={{ backgroundColor: tag.cor }}>
                {ativa && <Check size={10} className="text-white" strokeWidth={3} />}
              </span>
              <span className="text-sm text-slate-700 flex-1 text-left">{tag.nome}</span>
            </button>
          )
        })}
      </div>
      <button onClick={onClose}
        className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600 transition-colors text-center">
        Fechar
      </button>
    </div>
  )
}

// ── Kanban Card ───────────────────────────────────────────────────────────────
function cardIgual(prev, next) {
  const p = prev.pedido, n = next.pedido
  return (
    p.id           === n.id           &&
    p.status       === n.status       &&
    p.prazo        === n.prazo        &&
    p.valorTotal   === n.valorTotal   &&
    p.cliente?.nome=== n.cliente?.nome&&
    p.vendedora    === n.vendedora    &&
    JSON.stringify(p.tags) === JSON.stringify(n.tags) &&
    prev.index     === next.index     &&
    prev.onClick   === next.onClick   &&
    prev.onToggleTag === next.onToggleTag &&
    prev.onDelete  === next.onDelete
  )
}

const KanbanCard = memo(function KanbanCard({ pedido, index, allTags, onClick, onToggleTag, onDelete }) {
  const [showTagPicker, setShowTagPicker] = useState(false)
  const vencido  = isPrazoVencido(pedido.prazo) && pedido.status !== 'POS_VENDA'
  const tagsCard = useMemo(() => allTags.filter(t => (pedido.tags || []).includes(t.id)), [allTags, pedido.tags])

  return (
    <Draggable draggableId={pedido.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(pedido.id)}
          className={`bg-white rounded-xl border p-3 cursor-pointer transition-shadow mb-2 select-none relative
            ${snapshot.isDragging
              ? 'shadow-xl border-[#F5C200] rotate-1'
              : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}
            ${vencido ? 'border-l-4 border-l-red-400' : ''}
          `}
        >
          {/* Tags chips */}
          {tagsCard.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {tagsCard.map(t => <TagChip key={t.id} tag={t} />)}
            </div>
          )}

          <div className="flex items-start justify-between gap-1 mb-1">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-mono text-slate-400">{pedido.numero}</p>
              {pedido.origem === 'bling' && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">Bling</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {vencido && <AlertCircle size={13} className="text-red-400 shrink-0" />}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onDelete(pedido.id) }}
                className="p-0.5 rounded hover:bg-red-50 text-slate-200 hover:text-red-400 transition-colors"
                title="Excluir pedido"
              >
                <Trash2 size={12} />
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); setShowTagPicker(s => !s) }}
                  className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-500 transition-colors"
                >
                  <Tag size={12} />
                </button>
                {showTagPicker && (
                  <TagPicker
                    pedidoId={pedido.id}
                    pedidoTags={pedido.tags || []}
                    allTags={allTags}
                    onToggle={onToggleTag}
                    onClose={() => setShowTagPicker(false)}
                  />
                )}
              </div>
            </div>
          </div>

          <p className="text-sm font-semibold text-slate-800 leading-tight mb-2">{pedido.cliente?.nome}</p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <Package size={11} />
              <span>{pedido.moveis?.length || 0} item{pedido.moveis?.length !== 1 ? 's' : ''}</span>
            </div>
            {pedido.prazo && (
              <div className={`flex items-center gap-1 text-xs ${vencido ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
                <Calendar size={11} />
                <span>{formatDate(pedido.prazo)}</span>
              </div>
            )}
          </div>

          <div className="mt-2 pt-2 border-t border-slate-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-[#CC8800]">{formatCurrency(pedido.valorTotal)}</span>
            {pedido.vendedora && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">
                {pedido.vendedora}
              </span>
            )}
          </div>

          {pedido.historico?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1 flex-wrap">
              {pedido.historico.map((h, i) => (
                <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold
                  ${h.tipo === 'ajuste' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                  {h.tipo === 'ajuste' ? '🔧' : '✓'} {formatDate(h.data)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Draggable>
  )
}, cardIgual)

// ── Modal de gestão de tags ───────────────────────────────────────────────────
function ModalGerenciarTags({ onClose }) {
  const [tags, setTags]         = useState(() => getTags())
  const [novoNome, setNovoNome] = useState('')
  const [novaCor, setNovaCor]   = useState(TAG_CORES[0])

  function handleCriar() {
    if (!novoNome.trim()) return
    const tag = { id: uuid(), nome: novoNome.trim(), cor: novaCor }
    saveTag(tag)
    setTags(getTags())
    setNovoNome('')
  }

  function handleDeletar(id) {
    deleteTag(id)
    setTags(getTags())
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-[#111111]">Gerenciar etiquetas</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Criar nova */}
        <div className="space-y-3 mb-5">
          <input
            value={novoNome}
            onChange={e => setNovoNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCriar()}
            className="input"
            placeholder="Nome da etiqueta..."
          />
          <div>
            <p className="text-xs text-slate-500 mb-2">Cor</p>
            <div className="flex flex-wrap gap-2">
              {TAG_CORES.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNovaCor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${novaCor === c ? 'scale-125 border-[#111111]' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <button
            onClick={handleCriar}
            disabled={!novoNome.trim()}
            className="btn-primary w-full justify-center text-sm"
          >
            <Plus size={14} />
            Criar etiqueta
          </button>
        </div>

        {/* Lista existente */}
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {tags.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">Nenhuma etiqueta ainda</p>
          )}
          {tags.map(tag => (
            <div key={tag.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50">
              <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: tag.cor }} />
              <span className="text-sm text-slate-700 flex-1">{tag.nome}</span>
              <button
                onClick={() => handleDeletar(tag.id)}
                className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Kanban principal ──────────────────────────────────────────────────────────
export function Kanban() {
  const navigate                        = useNavigate()
  const { pedidos, moverStatus, remover, refresh } = useOrders()
  const [allTags, setAllTags]           = useState(() => getTags())
  const [modalTags, setModalTags]       = useState(false)
  const [showFiltro, setShowFiltro]     = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [cidadeAba, setCidadeAba]       = useState('TODAS')
  const [mostrarEntregue, setMostrarEntregue] = useState(false)

  const { filtros, totalAtivos, limpar, toggle, set, filtrar } = usePedidosFiltros()

  const onDragEnd = useCallback((result) => {
    const { draggableId, destination } = result
    if (!destination) return
    const novoStatus = destination.droppableId
    const pedidoAnterior = pedidos.find(p => p.id === draggableId)

    if (pedidoAnterior && !String(draggableId).startsWith('bling-')) {
      let historico = [...(pedidoAnterior.historico || [])]
      if (novoStatus === 'AJUSTE' && pedidoAnterior.status !== 'AJUSTE') {
        historico.push({ tipo: 'ajuste', data: new Date().toISOString().split('T')[0] })
      } else if (pedidoAnterior.status === 'AJUSTE' && novoStatus === 'ENTREGUE') {
        historico.push({ tipo: 'reentregue', data: new Date().toISOString().split('T')[0] })
      }
      if (historico.length !== (pedidoAnterior.historico || []).length) {
        savePedido({ ...pedidoAnterior, historico, status: novoStatus })
        refresh()
        return
      }
    }

    moverStatus(draggableId, novoStatus)
  }, [moverStatus, pedidos, refresh])

  const handleToggleTag = useCallback((pedidoId, tagId) => {
    if (String(pedidoId).startsWith('bling-')) return
    toggleTagPedido(pedidoId, tagId)
    refresh()
  }, [refresh])

  const handleFecharModalTags = useCallback(() => {
    setModalTags(false)
    setAllTags(getTags())
    refresh()
  }, [refresh])

  const handleDelete = useCallback((id) => {
    remover(id)
    setConfirmDelete(null)
  }, [remover])

  const handleCardClick = useCallback((id) => navigate(`/pedidos/${id}`), [navigate])

  const pedidos2026      = useMemo(() => pedidos.filter(p => p.prazo?.startsWith('2026')), [pedidos])
  const pedidosFiltrados = useMemo(() => {
    let lista = filtrar(pedidos2026)
    if (!mostrarEntregue) lista = lista.filter(p => p.status !== 'ENTREGUE' && p.status !== 'POS_VENDA')
    if (cidadeAba !== 'TODAS') lista = lista.filter(p => detectarCidadeAba(p) === cidadeAba)
    return lista
  }, [pedidos2026, filtrar, mostrarEntregue, cidadeAba])

  const pedidosPorStatus = KANBAN_COLUNAS.reduce((acc, col) => {
    acc[col.id] = pedidosFiltrados
      .filter(p => p.status === col.id)
      .sort((a, b) => {
        if (!a.prazo && !b.prazo) return 0
        if (!a.prazo) return 1
        if (!b.prazo) return -1
        return new Date(a.prazo) - new Date(b.prazo)
      })
    return acc
  }, {})

  return (
    <div className="px-4 py-8 h-[calc(100vh-64px)] flex flex-col transition-all" style={showFiltro ? { paddingRight: 296 } : {}}>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Kanban de produção</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {pedidosFiltrados.length} pedido{pedidosFiltrados.length !== 1 ? 's' : ''}
            {totalAtivos > 0 && ` · ${totalAtivos} filtro${totalAtivos > 1 ? 's' : ''} ativo${totalAtivos > 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">

          {/* Filtros */}
          <div>
            <button
              onClick={() => setShowFiltro(o => !o)}
              className={`btn-secondary text-sm ${showFiltro ? 'ring-2 ring-[#F5C200]' : ''}`}
            >
              <Filter size={14} />
              Filtrar
              {totalAtivos > 0 && (
                <span className="ml-1 bg-[#F5C200] text-[#111111] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {totalAtivos}
                </span>
              )}
            </button>
          </div>

          <button onClick={() => setModalTags(true)} className="btn-secondary text-sm">
            <Tag size={14} />
            Etiquetas
          </button>
        </div>
      </div>

      <FiltroDrawer
        open={showFiltro}
        onClose={() => setShowFiltro(false)}
        filtros={filtros}
        toggle={toggle}
        set={set}
        limpar={limpar}
        totalAtivos={totalAtivos}
        pedidos={pedidos2026}
        sections={{ tags: true }}
        allTags={allTags}
      />

      {/* Abas de cidade */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {CIDADES_ABAS.map(aba => (
            <button key={aba.id} onClick={() => setCidadeAba(aba.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                ${cidadeAba === aba.id ? 'bg-white text-[#111111] shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-700'}`}>
              {aba.label}
            </button>
          ))}
        </div>
        <button onClick={() => setMostrarEntregue(v => !v)}
          className={`btn-secondary text-sm ${mostrarEntregue ? 'ring-2 ring-pink-300' : ''}`}>
          {mostrarEntregue ? <Eye size={14} /> : <EyeOff size={14} />}
          Entregues
        </button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 flex-1 items-start">
          {KANBAN_COLUNAS.map(col => {
            const itens = pedidosPorStatus[col.id] || []
            return (
              <div key={col.id} className="flex flex-col shrink-0 w-64">
                <div className={`flex items-center justify-between px-3 py-2 rounded-t-xl border border-b-0 ${col.color}`}>
                  <span className="text-xs font-bold uppercase tracking-wide">{col.label}</span>
                  <span className="text-xs font-bold bg-white/60 px-2 py-0.5 rounded-full">{itens.length}</span>
                </div>
                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 min-h-32 p-2 rounded-b-xl border border-t-0 transition-colors
                        ${col.color.split(' ')[0]}
                        ${snapshot.isDraggingOver ? 'ring-2 ring-inset ring-[#F5C200]' : 'border-slate-200'}
                      `}
                    >
                      {itens.map((p, i) => (
                        <KanbanCard
                          key={p.id}
                          pedido={p}
                          index={i}
                          allTags={allTags}
                          onClick={handleCardClick}
                          onToggleTag={handleToggleTag}
                          onDelete={setConfirmDelete}
                        />
                      ))}
                      {provided.placeholder}
                      {itens.length === 0 && !snapshot.isDraggingOver && (
                        <p className="text-xs text-slate-300 text-center py-4">Vazio</p>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            )
          })}
        </div>
      </DragDropContext>

      {modalTags && <ModalGerenciarTags onClose={handleFecharModalTags} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-slate-800 mb-2">Excluir pedido?</h3>
            <p className="text-sm text-slate-500 mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary flex-1 justify-center">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} className="btn-danger flex-1 justify-center">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
